import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import { ArrowRight, CheckCircle, Circle } from 'lucide-react'
import Banner from '../../Components/Banner';
import { Helmet } from 'react-helmet-async';

const featuresData = [
    {
        id: 1,
        title: "Implementation & Rollouts",
        description:
            "We deliver greenfield builds, global rollouts, and multi-site deployments with proven, practical best practices.",
        image: "/assets/v/item1.png",
    },
    {
        id: 2,
        title: "Upgrade & Migration Services",
        description:
            "Effortless migration to the latest M3 CloudSuite version with almost no disruption to your business.",
        image: "/assets/v/item2.png",
    },
    {
        id: 3,
        title: "Integration Services",
        description:
            "Smoothly connect Infor M3 with your CRM, e-commerce, and supply chain tools for effortless, unified business operations.",
        image: "/assets/v/item3.png",
    },
    {
        id: 4,
        title: "Customization & Extension",
        description:
            "Tailored, practical enhancements using Infor Mongoose, ION, and Infor OS to match your unique business needs.",
        image: "/assets/v/item4.png",
    },
    {
        id: 5,
        title: "Managed Services & Support",
        description:
            "Full support from monitoring and maintenance to upgrades and user training, so your system always runs smoothly and your team stays confident using it.",
        image: "/assets/v/item6.png",
    },
    {
        id: 6,
        title: "Business Process Consulting",
        description:
            "We help optimize your processes with industry-aligned guidance to maximize ERP value and returns.",
        image: "/assets/v/item7.jpg",
    },
];

const toolsData = [
    {
        id: 1,
        title: "Cloud",
        desc: "AWS, Azure, Google Cloud",
    },
    {
        id: 2,
        title: "Data Platforms",
        desc: "Snowflake, BigQuery, Redshift, Synapse",
    },
    {
        id: 3,
        title: "BI & Visualization",
        desc: "Power BI, Tableau, Looker",
    },
    {
        id: 4,
        title: "AI & ML",
        desc: "Python, TensorFlow, PyTorch",
    },
    {
        id: 5,
        title: "Integration",
        desc: "Kafka, Airflow, dbt",
    },
];

const outcomesData = [
    {
        id: 1,
        text: "Faster, data-driven decision-making",
    },
    {
        id: 2,
        text: "Single source of truth across the enterprise",
    },
    {
        id: 3,
        text: "Predictive insights & automation",
    },
    {
        id: 4,
        text: "Improved efficiency, cost control & revenue growth",
    },
];

const whyMobikodePoints1 = [
    "Fashion & Apparel solutions",
    "Food & Beverage industry support",
    "Industrial Manufacturing services",
    "Equipment & Rental solutions",
    "Chemicals & Distribution networks",
];

const whyMobikodePoints2 = [
    "Faster go-live with ready-to-use accelerators",
    "Global delivery backed by strong local support",
    "Deep domain knowledge with proven process expertise",
    "Consistent track record of on-time, on-budget delivery",
];

const approachData = [
    {
        id: 1,
        step: "01",
        title: "Understand the Business",
        desc: "KPIs, pain points, outcomes",
    },
    {
        id: 2,
        step: "02",
        title: "Engineer the Data",
        desc: "Clean, trusted, scalable pipelines",
    },
    {
        id: 3,
        step: "03",
        title: "Analyze & Model",
        desc: "Advanced analytics & AI",
    },
    {
        id: 4,
        step: "04",
        title: "Visualize & Enable",
        desc: "Dashboards, alerts, self-service",
    },
    {
        id: 5,
        step: "05",
        title: "Optimize Continuously",
        desc: "Governance, performance & adoption",
    },
];

function InForService() {
    return (
        <div>
            <Helmet>
    <title>
        Infor M3 Solutions & Consulting Services | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode provides Infor M3 solutions and consulting services covering implementation, upgrades, migration, integration, customization, managed support, and business process optimization for modern enterprises."
    />

    <meta
        name="keywords"
        content="Infor M3 Solutions, Infor M3 Consulting Services, Infor M3 Implementation, Infor M3 Upgrade Services, Infor M3 Migration Services, Infor M3 Integration Services, Infor M3 Customization, Infor M3 Managed Services, Infor M3 Support Services, Infor M3 Business Process Consulting, Infor M3 CloudSuite, Infor M3 ERP Services, Infor M3 Consultant, Infor M3 Manufacturing Solutions, Infor M3 Distribution Solutions"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/infor-m3-solutions"
    />

    <meta
        property="og:title"
        content="Infor M3 Solutions & Consulting Services | Mobikode"
    />

    <meta
        property="og:description"
        content="Optimize your enterprise operations with Mobikode's Infor M3 solutions, from implementation and migration to integration, customization, managed services, and business process consulting."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/infor-m3-solutions"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/service-detail/img5.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Infor M3 Solutions & Consulting Services | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Explore Mobikode's Infor M3 expertise across implementation, CloudSuite migration, integrations, customizations, managed support, and business process optimization."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/service-detail/img5.png"
    />
</Helmet>
            <AiHero title="Infor M3 Solutions" imgData="/assets/service-detail/img5.png" />
            <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/v/item5.png"
                                alt="ai automation"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">
                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                At Mobikode, we work closely with global enterprises to deliver practical, end-to-end Infor M3 solutions that actually fit their day-to-day operations. Whether it’s manufacturing, distribution, or service-based businesses, we bring real implementation experience and deep product knowledge to simplify complexity and improve performance. Our focus is not just on getting Infor M3 up and running, but on making it work smoothly for your teams today while staying flexible enough to support your growth tomorrow. </p>
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>
                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>
                        </div>
                    </div>
                </div>
            </section>
            <WhyAIAutomation title="Our Core Offerings" fData={featuresData} />
            {/* <TechServicesSection title="Explore Our Services" sData={services} /> */}
            {/* <IndustriesWeServe /> */}

            <section className="w-full bg-white pb-10 lg:pb-10 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-[30px] gap-[16px]">
                        <div className="bg-[#F9F9F9] p-4 lg:p-8 border border-gray-100">
                            <div className="max-w-[1000px] mx-auto">
                                <div className="text-left mb-10">
                                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4">
                                        Industry Focus
                                    </h2>
                                </div>

                                {/* Checkpoints Grid */}
                                <div className="grid grid-cols-1 gap-y-4 gap-x-12">
                                    {whyMobikodePoints1.map((point, index) => (
                                        <div key={index} className="flex items-center gap-3 group">
                                            <div className="mt-1">
                                                <Circle size={10} className="text-[#F06115] fill-[#F06115]/10" />
                                            </div>
                                            <p className="text-[#01386A] font-graphik lg:text-[16px] text-[14px] font-[500] group-hover:text-[#F06115] transition-colors duration-300">
                                                {point}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="bg-[#F9F9F9] p-4 lg:p-8 border border-gray-100">
                            <div className="max-w-[1000px] mx-auto">
                                <div className="text-left mb-10">
                                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-4">
                                        Why Mobikode?
                                    </h2>
                                    {/* <p className="text-[#49577A] font-graphik text-[16px] lg:text-[18px]">
                                    We don't just supply tools; we provide business-first analytics that scale with you.
                                </p> */}
                                </div>

                                {/* Checkpoints Grid */}
                                <div className="grid grid-cols-1 gap-y-4 gap-x-12">
                                    {whyMobikodePoints2.map((point, index) => (
                                        <div key={index} className="flex items-center gap-3 group">
                                            <div className="mt-1">
                                                <Circle size={10} className="text-[#F06115] fill-[#F06115]/10" />
                                            </div>
                                            <p className="text-[#01386A] font-graphik lg:text-[16px] text-[14px] font-[500] group-hover:text-[#F06115] transition-colors duration-300">
                                                {point}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
            {/* <section className="relative w-full h-[400px] lg:h-[507px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/career/img12.png"
                        alt="Agentic AI Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] h-full flex items-center lg:items-center ">
                    <div className="bg-white p-8 lg:p-12 max-w-[588px] shadow-2xl rounded-sm">

                        <h2 className="text-[#01386A] text-[30px] lg:text-[40px] font-[600] font-sora leading-tight mb-6">
                            Infor M3 Solutions
                        </h2>
                        <p className="text-[#49577A] text-[16px] leading-[24px] font-graphik mb-8">
                           At Mobikode, we help you simplify manufacturing and distribution with Infor M3 services. Our goal is to make your operations smoother, more agile, and ready for the cloud so your business can run efficiently and grow without unnecessary complexity.
   </p>
                        <a href="/contact" className="inline-flex items-center gap-2 text-[#01386A] underline font-graphik font-semibold text-lg hover:gap-3 transition-all duration-300 group">
                            Contact Now
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </a>
                    </div>
                </div>
            </section> */}
            <Banner title="Infor M3 Solutions" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
        </div>
    )
}

export default InForService
